import { useApolloClient } from '@apollo/react-hooks';
import { Typography } from '@bowtie-ins/bowkit';
import { Close } from '@bowtie-ins/bowkit/icons';
import {
  LIST_REWARD_SET,
  Promotion,
  RewardSet,
  Coupon,
} from '@bowtie-web/common';
import { Dialog, DialogProps } from '@bowtie-web/components';
import { Box, IconButton, useMediaQuery, useTheme } from '@material-ui/core';
import { compact, flatten } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsyncFn } from 'react-use';
import { CouponOption } from '../CouponForm';
import AnimatedDot from '../components/AnimatedDot';
import MenuItem from './MenuItem';

type CouponsMenuProps = {
  publicCouponOptions: CouponOption[];
  existingCustomerCouponOptions: CouponOption[];
  cachedCouponOptions: Promotion[];
  savedCouponCodes?: string[];
  savedCoupons?: Coupon[];
  hasNewlySavedCoupon?: boolean;
  onCouponSelected: (couponCode: string) => void;
  promotionSelected?: Promotion;
} & Pick<DialogProps, 'onClose' | 'open'>;

const CouponsMenu = ({
  publicCouponOptions = [],
  existingCustomerCouponOptions = [],
  cachedCouponOptions = [],
  savedCouponCodes = [],
  savedCoupons = [],
  hasNewlySavedCoupon = false,
  onCouponSelected,
  promotionSelected,
  onClose,
  open,
}: CouponsMenuProps) => {
  const theme = useTheme();
  const watchMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const intl = useIntl();
  const client = useApolloClient();
  const couponSelected = promotionSelected?.promo_code || '';

  // Separate saved coupons into two categories
  const { savedFromDefault, savedCustomCodes } = useMemo(() => {
    const savedFromDefault: CouponOption[] = [];
    const savedCustomCodes: string[] = [];

    savedCouponCodes.forEach(code => {
      const defaultOption = [
        ...publicCouponOptions,
        ...existingCustomerCouponOptions,
      ].find(({ couponCode }) => couponCode === code);

      if (defaultOption) {
        savedFromDefault.push(defaultOption);
      } else {
        savedCustomCodes.push(code);
      }
    });

    return { savedFromDefault, savedCustomCodes };
  }, [savedCouponCodes, publicCouponOptions, existingCustomerCouponOptions]);

  // Fetch reward sets for custom saved coupon codes
  const [{ value: customRewardSets }, fetchCustomRewards] = useAsyncFn(
    async (couponCodes: string[]) => {
      const results = await Promise.all(
        couponCodes.map(async couponCode => {
          try {
            const { data } = await client.query<{ rewardSets: RewardSet[] }>({
              query: LIST_REWARD_SET,
              variables: { coupon_code: couponCode },
            });

            return data.rewardSets && data.rewardSets.length > 0
              ? data.rewardSets.map(set => ({
                  ...set,
                  coupon_code: couponCode,
                }))
              : null;
          } catch {
            return null;
          }
        }),
      );

      return flatten(compact(results));
    },
    [client],
  );

  // Fetch reward sets when saved custom codes change
  useEffect(() => {
    if (savedCustomCodes.length > 0) {
      fetchCustomRewards(savedCustomCodes);
    }
  }, [savedCustomCodes, fetchCustomRewards]);

  const nonDefaultTypeSavedCouponOptions = useMemo(() => {
    const defaultCouponOptions = [
      ...publicCouponOptions,
      ...existingCustomerCouponOptions,
    ].map(({ couponCode }) => couponCode);

    return cachedCouponOptions.filter(
      ({ promo_code }) =>
        !defaultCouponOptions.includes(promo_code) &&
        !savedCouponCodes.includes(promo_code),
    );
  }, [
    existingCustomerCouponOptions,
    publicCouponOptions,
    cachedCouponOptions,
    savedCouponCodes,
  ]);

  const handleClick = useCallback(
    (couponCode: string) => {
      onCouponSelected(couponCode);
      onClose?.({}, 'backdropClick');
    },
    [onClose, onCouponSelected],
  );

  const filteredPublicCouponOptions = useMemo(() => {
    return publicCouponOptions.filter(
      ({ couponCode }) => !savedCouponCodes.includes(couponCode),
    );
  }, [publicCouponOptions, savedCouponCodes]);

  const filteredExistingCustomerCouponOptions = useMemo(() => {
    return existingCustomerCouponOptions.filter(
      ({ couponCode }) => !savedCouponCodes.includes(couponCode),
    );
  }, [existingCustomerCouponOptions, savedCouponCodes]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box
        display="flex"
        flexDirection="column"
        gridGap={watchMobile ? 16 : 32}
        p={watchMobile ? 2 : 3}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant={watchMobile ? 'title-xs' : 'title-m'}
            fontWeight="bold"
          >
            <FormattedMessage id="coupon.coupon_menu.title.header" />
          </Typography>
          <IconButton
            disableRipple
            onClick={() => onClose?.({}, 'backdropClick')}
            style={{ width: 30, height: 30 }}
            data-ui-component="Button"
            data-ui-variant="Quotation Coupons Menu"
            data-ui-action="Close"
          >
            <Close />
          </IconButton>
        </Box>
        {(savedFromDefault.length > 0 || savedCustomCodes.length > 0) && (
          <Box display="flex" flexDirection="column" gridGap={8}>
            <Box display="flex" alignItems="center" gridGap={8}>
              <Typography
                variant={watchMobile ? 'title-xs' : 'title-m'}
                fontWeight="bold"
              >
                <FormattedMessage id="coupon.coupon_menu.title.saved" />
              </Typography>
              <AnimatedDot visible={hasNewlySavedCoupon} />
            </Box>
            <Box>
              {/* Render saved coupons from default options */}
              {savedFromDefault.map(
                ({ defaultCouponMapping, couponCode, rewardSet }) => {
                  const savedCoupon = savedCoupons.find(
                    coupon => coupon.code === couponCode,
                  );
                  return (
                    <MenuItem
                      key={`saved-default-${defaultCouponMapping.type.code}`}
                      variant="saved"
                      couponCode={couponCode}
                      message={rewardSet.message}
                      displayEffectiveFrom={savedCoupon?.effective_from}
                      displayEffectiveTo={
                        savedCoupon?.effective_to ||
                        rewardSet.display_effective_to
                      }
                      defaultCouponMapping={defaultCouponMapping}
                      isSelected={couponSelected === couponCode}
                      onClick={handleClick}
                      dataUiComponent="Select"
                      dataUiVariant="Saved Coupon Option"
                    />
                  );
                },
              )}

              {/* Render custom saved coupons */}
              {savedCustomCodes.map(couponCode => {
                const rewardSet = customRewardSets?.find(
                  set => set.coupon_code === couponCode,
                );
                const savedCoupon = savedCoupons.find(
                  coupon => coupon.code === couponCode,
                );
                return (
                  <MenuItem
                    key={`saved-custom-${couponCode}`}
                    variant="saved"
                    couponCode={couponCode}
                    message={rewardSet?.message || ''}
                    displayEffectiveFrom={savedCoupon?.effective_from}
                    displayEffectiveTo={
                      savedCoupon?.effective_to ||
                      rewardSet?.display_effective_to
                    }
                    isSelected={couponSelected === couponCode}
                    onClick={handleClick}
                    dataUiComponent="Select"
                    dataUiVariant="Saved Coupon Option"
                  />
                );
              })}
            </Box>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Typography
            variant={watchMobile ? 'title-xs' : 'title-m'}
            fontWeight="bold"
          >
            <FormattedMessage id="coupon.coupon_menu.title.public" />
          </Typography>
          <Box>
            {filteredPublicCouponOptions.map(
              ({ defaultCouponMapping, couponCode, rewardSet }) => (
                <MenuItem
                  key={defaultCouponMapping.type.code}
                  variant="default"
                  couponCode={couponCode}
                  message={rewardSet.message}
                  displayEffectiveTo={rewardSet.display_effective_to}
                  defaultCouponMapping={defaultCouponMapping}
                  isSelected={couponSelected === couponCode}
                  onClick={handleClick}
                  dataUiComponent="Select"
                  dataUiVariant="Public Coupon Option"
                />
              ),
            )}
            {nonDefaultTypeSavedCouponOptions.map(
              ({ promo_code, message, display_effective_to }) => (
                <MenuItem
                  key={promo_code}
                  variant="custom-code"
                  couponCode={promo_code}
                  message={message}
                  displayEffectiveTo={display_effective_to}
                  isSelected={couponSelected === promo_code}
                  onClick={handleClick}
                  dataUiComponent="Select"
                  dataUiVariant="User Input Coupon Option"
                />
              ),
            )}
            <MenuItem
              key="enter-coupon-code"
              variant="enter-code"
              couponCode=""
              message={intl.formatMessage({ id: 'coupon.coupon_code' })}
              onClick={handleClick}
              dataUiComponent="Select"
              dataUiVariant="User Entering Coupon Code"
            />
          </Box>
        </Box>
        {filteredExistingCustomerCouponOptions.length > 0 && (
          <Box display="flex" flexDirection="column" gridGap={8}>
            <Typography
              variant={watchMobile ? 'title-xs' : 'title-m'}
              fontWeight="bold"
            >
              <FormattedMessage id="coupon.coupon_menu.title.existing_customer" />
            </Typography>
            <Box>
              {filteredExistingCustomerCouponOptions.map(
                ({ defaultCouponMapping, couponCode, rewardSet }) => (
                  <MenuItem
                    key={defaultCouponMapping.type.code}
                    variant="default"
                    couponCode={couponCode}
                    message={rewardSet.message}
                    displayEffectiveTo={rewardSet.display_effective_to}
                    defaultCouponMapping={defaultCouponMapping}
                    isSelected={couponSelected === couponCode}
                    onClick={handleClick}
                    dataUiComponent="Select"
                    dataUiVariant="Existing Customer Coupon Option"
                  />
                ),
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CouponsMenu;
